import { api, type ProcessingViewResponse, type RedactionStatus, type RoomDocument } from '@dataroom/shared';

/**
 * The REDACTION REVIEW screen's data (owner, 2026-10-15: "When it says
 * 'Redaction needs review', that's nice, but there is nowhere to review the
 * redaction"). UI over the existing review API (src/modules/http/routes/
 * redaction.ts, ADR-0057) — the room admin and the bound seller only, over
 * documents they can see; nothing here decides who sees what:
 *
 *   GET   /rooms/:roomId/documents/:documentId/redaction        the live run + its marks
 *   PATCH /rooms/:roomId/redaction/marks/:markId               { decision: 'approved' | 'rejected' }
 *   POST  /rooms/:roomId/documents/:documentId/redaction/marks  { pageNumber, boxes: [{x,y,w,h}] }
 *   POST  /rooms/:roomId/documents/:documentId/redaction/apply  → 202 (409 redaction_needs_redetect
 *                                                                after a run that ended needs_review)
 *   POST  /rooms/:roomId/documents/:documentId/redaction/detect → 202 (a NEW run; a person's
 *                                                                decisions carry over)
 *   POST  /rooms/:roomId/documents/:documentId/redaction/approve → 202 / 200 ("Redaction is
 *                                                                right — approve": undecided
 *                                                                marks are hidden, the
 *                                                                approval is recorded)
 *
 * Geometry is NORMALIZED to the upright page (as a reader sees it): x and y run
 * 0..1 from the top-left corner (src/modules/redaction/types.ts). A drawn box
 * must be at least 0.002 wide and tall and lie on the page.
 */

export type RedactionRunStatus =
  | 'queued'
  | 'detecting'
  | 'applying'
  | 'ready'
  | 'not_needed'
  | 'unsupported'
  | 'needs_review'
  | 'failed'
  | 'superseded';

/** Why a copy could not be made safely (engine.ts REDACTION_FAILURE_REASONS) — content-free. */
export type RedactionFailureReason =
  | 'unreadable'
  | 'page_unrenderable'
  | 'timeout'
  | 'out_of_memory'
  | 'ocr_failed'
  | 'verify_failed'
  | 'held_format'
  | 'error';

/** Why a Word / Excel file is held (src/modules/redaction/types.ts OFFICE_HELD_REASONS). */
export type RedactionHeldReason =
  'macros' | 'encrypted' | 'legacy' | 'unreadable' | 'embedded' | 'image';

/** Which verify check a `verify_failed` run failed (src/modules/redaction/types.ts VERIFY_CHECKS), in plain words. */
const VERIFY_CHECK_WORDS: Partial<Record<string, string>> = {
  annotations: 'a comment or form field was left on the page',
  text_layer: 'hidden text under a box still held it',
  hidden_value: 'it still appeared elsewhere in the file',
  mark_not_dark: 'a box was not solid black',
  check_zone: 'a check had no box over it',
  text_survival: 'too much of the page’s other text was lost',
  unredacted_clear: 'no box covered it',
  residual_objects: 'old content of the page was left in the file',
};

/**
 * `auto` — applied with no review; `approved` — a person chose to hide it;
 * `pending` — waiting for a person; `rejected` — a person chose to keep it
 * visible; `dismissed` — the AI judged it not personal and left it visible.
 */
export type MarkDecision = 'auto' | 'pending' | 'approved' | 'rejected' | 'dismissed';

export interface RedactionBox {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** `runView` in routes/redaction.ts. */
export interface RedactionRunView {
  runId: string;
  versionId: string;
  status: RedactionRunStatus;
  failureReason: RedactionFailureReason | null;
  failurePage: number | null;
  /**
   * `held_format`: the one reason this file is held; `verify_failed`: the
   * check that failed (null for older runs).
   */
  failureDetail?: string | null;
  /** Who approved this run's redaction, and when (null: nobody yet). */
  approvedBy?: string | null;
  approvedAt?: string | null;
  /** The run already started over once by itself after a failure. */
  autoRetried?: boolean;
  trigger: 'filing' | 'manual';
  /** The step the run is on while it works (`detect` / `apply`), else null. */
  phase: 'detect' | 'apply' | null;
  pagesDone: number;
  pageCount: number | null;
  scannedPages: number | null;
  /** A person changed a decision since the copy was made. */
  changesPending: boolean;
  accountContext: string;
  updatedAt: string;
  finishedAt: string | null;
}

/** `markView` in routes/redaction.ts. `preview` is already masked ("•••-••-6789"). */
export interface RedactionMarkView {
  markId: string;
  pageNumber: number;
  boxes: RedactionBox[];
  category: string;
  reason: string;
  source: 'pattern' | 'manual';
  certainty: 'clear' | 'uncertain' | 'manual';
  decision: MarkDecision;
  keepLast: number;
  preview: string;
  aiProbability: number | null;
  decidedAt: string | null;
}

export interface DocumentRedaction {
  documentId: string;
  run: RedactionRunView | null;
  marks: RedactionMarkView[];
}

// ------------------------------------------------------------------ requests

export const redactionQueryKey = (roomId: string, documentId: string) =>
  ['rooms', roomId, 'redaction', documentId] as const;
export const redactionReviewListKey = (roomId: string) =>
  ['rooms', roomId, 'redaction', 'review-list'] as const;

export function fetchDocumentRedaction(roomId: string, documentId: string): Promise<DocumentRedaction> {
  return api.get<DocumentRedaction>(`/rooms/${roomId}/documents/${documentId}/redaction`);
}

export function decideRedactionMark(
  roomId: string,
  markId: string,
  decision: 'approved' | 'rejected',
): Promise<{ markId: string; decision: string }> {
  return api.patch(`/rooms/${roomId}/redaction/marks/${markId}`, { decision });
}

export function addRedactionBox(
  roomId: string,
  documentId: string,
  pageNumber: number,
  box: RedactionBox,
): Promise<{ markId: string }> {
  return api.post(`/rooms/${roomId}/documents/${documentId}/redaction/marks`, {
    pageNumber,
    boxes: [box],
  });
}

export function applyRedaction(roomId: string, documentId: string): Promise<{ runId: string }> {
  return api.post(`/rooms/${roomId}/documents/${documentId}/redaction/apply`);
}

export function detectRedaction(roomId: string, documentId: string): Promise<{ runId: string }> {
  return api.post(`/rooms/${roomId}/documents/${documentId}/redaction/detect`);
}

/** "Redaction is right — approve": the marks as shown (undecided ones hidden), recorded. */
export function approveRedaction(
  roomId: string,
  documentId: string,
): Promise<{ runId: string; hidden: number; applying: boolean }> {
  return api.post(`/rooms/${roomId}/documents/${documentId}/redaction/approve`);
}

/** The review screen's address for one document. */
export function redactionReviewPath(roomId: string, documentId: string): string {
  return `/rooms/${roomId}/redaction?doc=${encodeURIComponent(documentId)}`;
}

/** Redaction states a person resolves on the review screen. */
export function redactionNeedsPerson(status: RedactionStatus | null | undefined): boolean {
  return status === 'needs_review' || status === 'failed';
}

/**
 * Every document in the room whose redaction needs a person — FRESH from the
 * server (the processing view's "Needs review" selection, which also holds
 * OCR failures; only the redaction ones are kept), oldest upload first so the
 * walk through them is stable.
 */
export async function fetchRedactionReviewList(roomId: string): Promise<RoomDocument[]> {
  const q = new URLSearchParams({
    status: 'needs_review',
    page: '1',
    pageSize: '200',
    order: 'asc',
  });
  const view = await api.get<ProcessingViewResponse>(`/rooms/${roomId}/processing?${q.toString()}`);
  return view.documents.filter((d) => redactionNeedsPerson(d.redactionStatus));
}

/** The next document still needing review after `currentId` (never `currentId` itself). */
export function nextNeedingReview(list: RoomDocument[], currentId: string): RoomDocument | null {
  return list.find((d) => d.id !== currentId) ?? null;
}

// ------------------------------------------------------------------ marks

/** What a mark does to the redacted copy. */
export type MarkState = 'hidden' | 'undecided' | 'visible';

export function markState(decision: MarkDecision): MarkState {
  if (decision === 'auto' || decision === 'approved') return 'hidden';
  if (decision === 'pending') return 'undecided';
  return 'visible';
}

const CATEGORY_LABEL: Record<string, string> = {
  ssn: 'Social Security number',
  itin: 'Taxpayer ID (ITIN)',
  bank_account: 'Account number',
  card_number: 'Card number',
  gov_id: 'ID number',
  dob: 'Date of birth',
  manual: 'Box you drew',
};

/** What a mark is, in plain words — never its value. */
export function markKind(mark: Pick<RedactionMarkView, 'category' | 'reason' | 'source'>): string {
  if (mark.source === 'manual') return CATEGORY_LABEL.manual!;
  if (mark.reason === 'check_micr') return 'Check account line';
  if (mark.reason === 'check_back') return 'Back of a check';
  return CATEGORY_LABEL[mark.category] ?? 'Personal data';
}

/**
 * The masked preview to show beside the kind: at most the LAST FOUR digits,
 * every earlier digit as "•" — whatever the server sends (it sends masked
 * previews; this never lets more through). Null when there is nothing to show
 * (a drawn box, or a preview with no digits).
 */
export function maskedPreview(mark: Pick<RedactionMarkView, 'preview' | 'source'>): string | null {
  if (mark.source === 'manual') return null;
  const digits = (mark.preview.match(/\d/g) ?? []).length;
  if (digits === 0) return null;
  let seen = 0;
  return mark.preview.replace(/\d/g, (d) => {
    seen += 1;
    return seen > digits - 4 ? d : '•';
  });
}

export interface PageGroup {
  pageNumber: number;
  marks: RedactionMarkView[];
}

/** The marks by page, pages in order (the server lists them page, then creation order). */
export function marksByPage(marks: RedactionMarkView[]): PageGroup[] {
  const pages = new Map<number, RedactionMarkView[]>();
  for (const m of marks) {
    const list = pages.get(m.pageNumber);
    if (list) list.push(m);
    else pages.set(m.pageNumber, [m]);
  }
  return [...pages.entries()]
    .sort(([a], [b]) => a - b)
    .map(([pageNumber, list]) => ({ pageNumber, marks: list }));
}

/** Marks still waiting for a person. */
export function undecidedCount(marks: RedactionMarkView[]): number {
  return marks.filter((m) => m.decision === 'pending').length;
}

// ------------------------------------------------------------------ the run

/** Where the review stands — drives the banner and the buttons. */
export type ReviewPhase =
  /** No redaction has run on this version. */
  | 'none'
  /** Detection or the copy is being made — the screen polls. */
  | 'working'
  /** The copy could not be made safely; detection must run again (apply is refused). */
  | 'stopped'
  /** This file type is not redacted. */
  | 'unsupported'
  /** Marks wait for a decision. */
  | 'decide'
  /** Every mark is decided; the redacted copy does not have the latest decisions yet. */
  | 'apply'
  /** The redacted copy has every decision. */
  | 'done';

export function reviewPhase(data: DocumentRedaction | undefined): ReviewPhase {
  const run = data?.run;
  if (!run) return 'none';
  switch (run.status) {
    case 'queued':
    case 'detecting':
    case 'applying':
      return 'working';
    case 'needs_review':
    case 'failed':
      return 'stopped';
    case 'unsupported':
      return 'unsupported';
    case 'superseded':
      return 'working';
    case 'ready':
    case 'not_needed':
    default:
      if (undecidedCount(data.marks) > 0) return 'decide';
      return run.changesPending ? 'apply' : 'done';
  }
}

/** The redaction state the buyer-copy viewer reads (as the listing would serve it). */
export function viewerRedactionStatus(data: DocumentRedaction | undefined): RedactionStatus | null {
  switch (reviewPhase(data)) {
    case 'none':
      return null;
    case 'working':
      return 'pending';
    case 'stopped':
      return data?.run?.status === 'failed' ? 'failed' : 'needs_review';
    case 'unsupported':
      return 'unsupported';
    case 'decide':
    case 'apply':
      return 'needs_review';
    case 'done':
      return 'complete';
  }
}

const on = (page: number | null) => (page ? ` on page ${page}` : '');

/** The file's extension, lower-case, without the dot ('' when it has none). */
function extensionOf(fileName: string | null | undefined): string {
  const base = (fileName ?? '').split(/[\\/]/).pop() ?? '';
  const dot = base.lastIndexOf('.');
  return dot > 0 ? base.slice(dot + 1).toLowerCase() : '';
}

/** Why a Word / Excel file is held: the ONE reason that applies to it, in plain words. */
export function heldReason(detail: RedactionHeldReason | null | undefined, fileName?: string | null): string {
  const ext = extensionOf(fileName);
  const word = ext.startsWith('doc');
  const kind = word ? 'Word' : 'Excel';
  const modern = word ? '.docx' : '.xlsx';
  switch (detail) {
    case 'macros':
      return `This file contains macros (program code), so it is never opened automatically. Save it as a ${modern} file without macros, or as a PDF, and upload that.`;
    case 'encrypted':
      return 'This file is password-protected, so it can’t be opened to make a redacted copy. Upload a copy without the password.';
    case 'legacy':
      return `This is an old-format ${kind} file (.${ext === 'doc' || ext === 'xls' ? ext : word ? 'doc' : 'xls'}), which is never opened automatically. Save it as ${modern} or PDF and upload that.`;
    case 'unreadable':
      return 'This file couldn’t be opened — it looks damaged. Upload a readable copy.';
    case 'embedded':
      return 'This file has another file inside it (such as a PowerPoint or an old Office object) that can’t be checked for personal data. Remove it, or upload the file as a PDF.';
    case 'image':
      return 'This file contains a picture in a format that can’t be checked for personal data (such as EMF, GIF or TIFF). Replace it with a PNG or JPEG picture, or upload the file as a PDF.';
    default:
      return 'This file couldn’t be opened safely to make a redacted copy. Upload it as a PDF.';
  }
}

/**
 * Why the run stopped, in plain words (the run carries a content-free reason
 * and the page it is about — never the value). One sentence for the reason
 * that applies.
 */
export function stopReason(
  run: Pick<RedactionRunView, 'status' | 'failureReason' | 'failurePage' | 'failureDetail' | 'autoRetried'>,
  fileName?: string | null,
): string {
  const page = run.failurePage;
  // It was already tried a second time by itself (the server retries these once).
  const again = run.autoRetried ? ', also when it was tried again automatically' : '';
  switch (run.failureReason) {
    case 'verify_failed': {
      const why = VERIFY_CHECK_WORDS[run.failureDetail ?? ''];
      const because = why ? ` (${why})` : '';
      return page
        ? `Personal data on page ${page} was still readable after blacking out${because} — check that the boxes on page ${page} cover it.`
        : `Personal data was still readable after blacking out${because} — check that the boxes cover it.`;
    }
    case 'ocr_failed':
      return `The scanned text${on(page)} couldn’t be read, so personal data there may have been missed — check that page and draw a box over anything personal.`;
    case 'page_unrenderable':
      return page
        ? `Page ${page} couldn’t be read to make the redacted copy — check it and draw a box over anything personal.`
        : 'A page couldn’t be read to make the redacted copy.';
    case 'unreadable':
      return 'The file couldn’t be opened to make a redacted copy — it may be damaged or password-protected. Upload a readable copy.';
    case 'timeout':
      return `Making the redacted copy took too long${on(page)}${again}.`;
    case 'out_of_memory':
      return `The document was too large to redact in one go${on(page)}${again}.`;
    case 'held_format':
      return heldReason(run.failureDetail as RedactionHeldReason | null | undefined, fileName);
    case 'error':
      return `Something went wrong making the redacted copy${on(page)}${again}.`;
    default:
      return run.status === 'failed'
        ? 'The redaction check could not finish.'
        : 'The redacted copy could not be made safely.';
  }
}

/**
 * A run that stopped because of the FILE — a held format or a file that can't
 * be opened: running detection again changes nothing; a new version does.
 */
export function stoppedForFile(run: Pick<RedactionRunView, 'failureReason'> | null | undefined): boolean {
  return run?.failureReason === 'held_format' || run?.failureReason === 'unreadable';
}

/** "Approved by Avery Testwell on Oct 16, 2026" — or null when nobody approved this run. */
export function approvalLine(run: Pick<RedactionRunView, 'approvedBy' | 'approvedAt'> | null | undefined): string | null {
  if (!run?.approvedAt) return null;
  const when = new Date(run.approvedAt);
  const date = Number.isNaN(when.getTime())
    ? ''
    : ` on ${when.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}`;
  return `Approved by ${run.approvedBy ?? 'a person'}${date}.`;
}

/** Progress of a working run, e.g. "page 3 of 12". */
export function runProgress(run: Pick<RedactionRunView, 'pagesDone' | 'pageCount'>): string | null {
  if (!run.pageCount || run.pageCount < 1) return null;
  return `page ${Math.min(Math.max(run.pagesDone, 0), run.pageCount)} of ${run.pageCount}`;
}

// ------------------------------------------------------------------ drawing

/** The API's smallest box side (routes/redaction.ts boxSchema). */
export const MIN_BOX_SIDE = 0.002;

const round = (v: number) => Math.round(v * 100_000) / 100_000;
const clamp01 = (v: number) => Math.min(1, Math.max(0, v));

/**
 * A drag from (x0, y0) to (x1, y1), both 0..1 of the page from its top-left
 * corner, as the API's box — clamped onto the page — or null when it is too
 * small to be a box.
 */
export function boxFromDrag(x0: number, y0: number, x1: number, y1: number): RedactionBox | null {
  const left = clamp01(Math.min(x0, x1));
  const top = clamp01(Math.min(y0, y1));
  const right = clamp01(Math.max(x0, x1));
  const bottom = clamp01(Math.max(y0, y1));
  const x = round(left);
  const y = round(top);
  const w = Math.min(round(right - left), round(1 - x));
  const h = Math.min(round(bottom - top), round(1 - y));
  if (w < MIN_BOX_SIDE || h < MIN_BOX_SIDE) return null;
  return { x, y, w, h };
}
